import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { formatCurrency, formatNumber, IMPUESTOS_LABELS, colorPara } from "../helpers"

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 200px",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "18px 20px", minWidth: 0,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)

StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const TooltipChart = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12, maxWidth: 320 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.nombre}</div>
      <div style={{ fontSize: 11, color: "#94a3b8" }}>NIT {d.nit}</div>
      <div style={{ marginTop: 4 }}>{formatCurrency(d.valor)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatNumber(d.facturas)} facturas</div>
    </div>
  )
}

TooltipChart.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ResumenTab = ({ data, onAbrirWizard }) => {
  const terceros = data?.terceros || []
  const resumen = data?.resumen || {}
  const metadata = data?.metadata || {}

  const top10 = useMemo(() => {
    return [...terceros]
      .sort((a, b) => (Number(b.total_ventas) || 0) - (Number(a.total_ventas) || 0))
      .slice(0, 10)
      .map(t => ({
        nombre: t.nombre_canonico || t.nombrereceptor || "—",
        nombreCorta: ((t.nombre_canonico || t.nombrereceptor || "—").length > 30
          ? (t.nombre_canonico || t.nombrereceptor || "—").slice(0, 29) + "…"
          : t.nombre_canonico || t.nombrereceptor || "—"),
        nit: t.nitreceptor,
        valor: Number(t.total_ventas) || 0,
        facturas: Number(t.total_facturas) || 0,
      }))
  }, [terceros])

  // Agregados de impuestos sumando todos los terceros
  const impuestosGlobales = useMemo(() => {
    const acc = {}
    IMPUESTOS_LABELS.forEach(({ key }) => { acc[key] = 0 })
    terceros.forEach(t => {
      IMPUESTOS_LABELS.forEach(({ key }) => {
        acc[key] += Number(t.detalle_impuestos?.[key]) || 0
      })
    })
    return IMPUESTOS_LABELS
      .map(({ key, label }) => ({ key, label, valor: acc[key] }))
      .filter(i => i.valor > 0)
      .sort((a, b) => b.valor - a.valor)
  }, [terceros])

  if (!data) return null

  // Ventas totales: preferimos el global del back (metadata.total_ventas_global)
  // que cubre TODOS los terceros, no solo los que vienen en la página actual.
  // Caemos al reduce solo si el back no lo entrega.
  const totalVentasGlobal = Number(metadata.total_ventas_global)
  const totalVentas = isFinite(totalVentasGlobal) && totalVentasGlobal > 0
    ? totalVentasGlobal
    : terceros.reduce((s, t) => s + (Number(t.total_ventas) || 0), 0)
  const totalFacturas = terceros.reduce((s, t) => s + (Number(t.total_facturas) || 0), 0)
  const totalImpuestos = terceros.reduce((s, t) => s + (Number(t.total_impuestos) || 0), 0)
  const conVariantes = terceros.filter(t => t.tiene_variantes).length

  const variantesPendientes = terceros.filter(t =>
    t.tiene_variantes && t.nombre_canonico_origen !== "manual",
  ).length
  const variantesValidadas = terceros.filter(t =>
    t.tiene_variantes && t.nombre_canonico_origen === "manual",
  ).length

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Banner Modo limpieza */}
      {conVariantes > 0 && (
        <div style={{
          background: variantesPendientes > 0
            ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
            : "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
          border: `1px solid ${variantesPendientes > 0 ? "#fcd34d" : "#6ee7b7"}`,
          borderRadius: 14,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: variantesPendientes > 0 ? "#f59e0b" : "#10b981",
            color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, flexShrink: 0,
          }}>
            <i className={`mdi mdi-${variantesPendientes > 0 ? "broom" : "check-circle"}`} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
              color: variantesPendientes > 0 ? "#92400e" : "#065f46",
              marginBottom: 2,
            }}>
              Limpieza de duplicados
            </div>
            <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 600 }}>
              {variantesPendientes > 0 ? (
                <>
                  <strong>{variantesPendientes}</strong> tercero{variantesPendientes !== 1 ? "s" : ""} con variantes de nombre sin validar
                </>
              ) : (
                <>Todos los terceros con variantes ya tienen nombre canónico validado manualmente</>
              )}
            </div>
            {variantesValidadas > 0 && (
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                <i className="mdi mdi-check-decagram text-success me-1" />
                {variantesValidadas} ya validado{variantesValidadas !== 1 ? "s" : ""} de {conVariantes} totales
              </div>
            )}
          </div>
          {variantesPendientes > 0 && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={() => onAbrirWizard?.()}
              style={{ fontWeight: 600, padding: "8px 18px" }}
            >
              <i className="mdi mdi-broom me-1" />
              Iniciar limpieza
            </button>
          )}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          icon="mdi-account-group"
          label="Terceros"
          value={formatNumber(terceros.length)}
          accent="#6366f1"
          sub={resumen.total_terceros ? `${formatNumber(resumen.total_terceros)} en BD` : null}
        />
        <StatCard
          icon="mdi-cash-multiple"
          label="Ventas totales"
          value={formatCurrency(totalVentas)}
          accent="#10b981"
        />
        <StatCard
          icon="mdi-receipt-text"
          label="Facturas"
          value={formatNumber(totalFacturas)}
          accent="#3b82f6"
        />
        <StatCard
          icon="mdi-bank"
          label="Impuestos"
          value={formatCurrency(totalImpuestos)}
          accent="#f59e0b"
        />
        <StatCard
          icon="mdi-ticket-percent-outline"
          label="Ticket promedio"
          value={formatCurrency(totalFacturas > 0 ? totalVentas / totalFacturas : 0)}
          accent="#8b5cf6"
        />
        <StatCard
          icon="mdi-account-alert-outline"
          label="Con variantes de nombre"
          value={
            <span style={{ color: conVariantes > 0 ? "#f59e0b" : "#10b981" }}>
              {formatNumber(conVariantes)}
            </span>
          }
          accent={conVariantes > 0 ? "#f59e0b" : "#10b981"}
          sub={conVariantes > 0 ? "Revisa la pestaña Variantes" : "Sin duplicados"}
        />
      </div>

      {/* Top 10 + Impuestos globales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {top10.length > 0 && (
          <div style={{ flex: "1 1 480px", background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0", minWidth: 0 }}>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
              <i className="mdi mdi-trophy me-2 text-warning" />
              Top 10 terceros por ventas
            </h6>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top10} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
                  <YAxis type="category" dataKey="nombreCorta" width={200} tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
                  <Tooltip content={<TooltipChart />} cursor={{ fill: "rgba(59,130,246,0.06)" }} />
                  <Bar dataKey="valor" radius={[0, 6, 6, 0]}>
                    {top10.map((d, i) => <Cell key={i} fill={colorPara(i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {impuestosGlobales.length > 0 && (
          <div style={{ flex: "1 1 360px", background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0", minWidth: 0 }}>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
              <i className="mdi mdi-bank me-2 text-warning" />
              Impuestos del período
            </h6>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {impuestosGlobales.map(imp => {
                  const pct = totalImpuestos > 0 ? (imp.valor / totalImpuestos) * 100 : 0
                  return (
                    <tr key={imp.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 4px", color: "#475569", fontWeight: 500 }}>{imp.label}</td>
                      <td style={{ padding: "8px 4px", textAlign: "right" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                          <div style={{ width: 60, height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: "#f59e0b" }} />
                          </div>
                          <span style={{ color: "#0f172a", fontWeight: 600, minWidth: 110, textAlign: "right" }}>
                            {formatCurrency(imp.valor)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Metadata footer */}
      {(metadata.fecha_inicio || metadata.fecha_fin) && (
        <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
          Período: {metadata.fecha_inicio || "—"} → {metadata.fecha_fin || "—"}
          {metadata.duracion ? ` · Procesado en ${metadata.duracion}` : ""}
        </div>
      )}
    </div>
  )
}

ResumenTab.propTypes = { data: PropTypes.object, onAbrirWizard: PropTypes.func }

export default ResumenTab
